import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Sort from '../../../../components/common/Sort';
import FilterButton from '../../../../components/common/FilterButton';
import DownloadCSVButton from '../../../../components/common/DownloadCSVButton';
import ComingSoon from '../../../../components/common/ComingSoon';
import { useIntentSignal } from '../../../../hooks/useIntentSignal';
import createIcon from '../../../../assets/create.svg';
import CreateIntentPopup from './CreateIntentPopup';
import { FilterCondition } from '../../../../types/filter';
import debounce from 'lodash/debounce';

interface IntentSignal {
  signal: string;
  [key: string]: any;
}

interface FilterBarProps {
  selectedSignals: any[];
  onDownloadCSV: () => void;
  isCreateIntentOpen: boolean;
  setIsCreateIntentOpen: (isOpen: boolean) => void;
  onSortChange: (option: string) => void;
  onSearch: (searchResults: any[]) => void;
  totalCount: number;
  onSearchQueryChange: (query: string) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ selectedSignals, onDownloadCSV, isCreateIntentOpen, setIsCreateIntentOpen, onSortChange, onSearch, totalCount, onSearchQueryChange }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([{
    column: '',
    operator: '',
    value: ''
  }]);
  const [allSignals, setAllSignals] = useState<any[]>([]);
  const { fetchAllSignalsForSearch } = useIntentSignal();

  const filterConfig = {
    columns: ['Signal Description', 'Status', 'Created On', 'Last Run', 'Lead Count'],
    conditions: filterConditions,
    onConditionsChange: (newConditions: FilterCondition[]) => {
      setFilterConditions(newConditions);
      console.log('Applied filters:', newConditions);
      // Handle the filter logic here
    }
  };

  const handleCreateIntent = () => {
    setIsCreateIntentOpen(true);
  };

  const handleDownloadCSV = () => {
    onDownloadCSV();
  };

  const performSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim()) {
        onSearch([]);
        return;
      }

      if (allSignals.length === 0) {
        try {
          const signals = await fetchAllSignalsForSearch();
          setAllSignals(signals);
          const results = signals.filter((signal: IntentSignal) => 
            signal.signal.toLowerCase().includes(query.toLowerCase())
          );
          onSearch(results);
        } catch (error) {
          console.error('Error fetching signals:', error);
        }
      } else {
        const results = allSignals.filter((signal: IntentSignal) => 
          signal.signal.toLowerCase().includes(query.toLowerCase())
        );
        onSearch(results);
      }
    }, 300),
    [allSignals, onSearch]
  );

  useEffect(() => {
    performSearch(searchQuery);
    onSearchQueryChange(searchQuery);
    return () => performSearch.cancel();
  }, [searchQuery, performSearch, onSearchQueryChange]);

  return (
    <div className='flex flex-col'>
      <div className="flex justify-between bg-white items-center py-[10px] px-6 border-b border-[#EEEEEE]">
        <div className='flex items-start'>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by intent signal"
              className="w-[360px] h-[28px] pl-[16px] pr-[36px] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-[#42E56F] focus:border-transparent text-[14px] placeholder:text-[#DDDDDD] font-light shadow-[0px_0px_2px_0px_#E0E0E0]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-[#EEEEEE] hover:bg-[#E0E0E0] transition-colors"
              >
                <span className="text-[#7A7A7A] text-sm leading-none pb-1">&times;</span>
              </button>
            )}
          </div>
        </div>
        <div className='flex items-end'>
          <DownloadCSVButton onClick={onDownloadCSV} />
        </div>
      </div>
      <div className='flex items-center justify-between bg-white py-[10px] px-6 border-b border-[#EEEEEE]'>
        <div className='flex items-center gap-2'>
            <Sort onSortChange={onSortChange} />
          {/* <div className="text-[#DDDDDD] text-[18px] text-center pb-[4px] font-thin">|</div>
          <div>
            <FilterButton filterConfig={filterConfig} />
          </div> */}
        </div>
        <button 
          data-create-intent="button"
          onClick={handleCreateIntent}
          className="create-intent-button flex items-center justify-center w-fit h-fit bg-[#29DC5B] text-[#222222] rounded-lg text-[Nunito Sans] text-[14px] font-light gap-[6px] transition-colors"
        >
          <div className="flex items-center justify-center gap-[6px] py-[4px] px-[8px] h-[28px]">
            <img src={createIcon} alt="Create Intent Signal" className="w-[14px] h-[14px] stroke-[#222222]" />
            <div className="text-[14px] font-normal">Create intent signal</div>
          </div>
        </button>
      </div>
      <CreateIntentPopup 
        isOpen={isCreateIntentOpen} 
        onClose={() => setIsCreateIntentOpen(false)} 
      />
      <ComingSoon 
        isOpen={showComingSoon}
        onClose={() => setShowComingSoon(false)}
      />
    </div>
  );
};

export default FilterBar; 